import React, { useEffect, useState } from "react";
import iconLogo from "../../assets/svg/ico-logo-typo.svg";
import iconMascot from "../../assets/svg/icon-mascot.svg";
import iconGoogleLogo from "../../assets/svg/icon-logo-google.svg";
import iconAppleLogo from "../../assets/svg/icon-logo-apple.svg";
import styled from "styled-components";
import Checkbox from "../../components/Checkbox";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { JWT_KEY } from "../../config/constant";
import { postAgreement } from "../../apis/agreement";
import { ISocialLoginResponse, Status } from "../../interface/auth";
import { getUserAgent, isLoggedIn } from "../../utils/utility";
import { getUserInfo, getUserProcess, patchUserStatus } from "../../apis/auth";
import { useModal } from "../../utils/useModal";

export default function Login() {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const [params] = useSearchParams();
  const [opened, setOpened] = useState(false);
  const [dimmed, setDimmmed] = useState(false);

  const { openModal } = useModal();

  const [form, setForm] = useState<IAgreement[]>([
    {
      id: "first",
      label: (
        <>
          (필수){" "}
          <span
            onClick={(e) => {
              e.stopPropagation();
              navigate("/mypage/terms/service");
            }}
          >
            이용약관
          </span>{" "}
          동의
        </>
      ),
      agreementTitle: "이용약관 동의",
      agreeYn: "N",
      required: "Y",
    },
    {
      id: "second",
      label: (
        <>
          (필수){" "}
          <span
            onClick={(e) => {
              e.stopPropagation();
              navigate("/mypage/terms/personal");
            }}
          >
            개인정보 수집 및 이용
          </span>{" "}
          동의
        </>
      ),
      agreementTitle: "개인정보 수집 및 이용 동의",
      agreeYn: "N",
      required: "Y",
    },
    {
      id: "last",
      label: (
        <>
          (선택){" "}
          <span
            onClick={(e) => {
              e.stopPropagation();
              navigate("/mypage/terms/marketing");
            }}
          >
            마케팅 정보 수신
          </span>{" "}
          동의
        </>
      ),
      agreementTitle: "마케팅 정보 수신 동의",
      agreeYn: "N",
      required: "N",
    },
  ]);

  const controlContainer = (mode: "open" | "close") => {
    if (mode === "close") {
      setOpened(false);
      setDimmmed(false);
    } else {
      setOpened(true);
      setDimmmed(true);
    }
  };

  /**
   * 구글 로그인 호출 함수 (웹: 페이지 이동 / 앱: 네이티브 로그인)
   */
  const signInWithGoogle = () => {
    if (window.Android) window.Android.googleLogin();
    else if (window.webkit)
      window.webkit?.messageHandlers?.googleLogin.postMessage("");
    else
      window.location.href = `${process.env.REACT_APP_API}/app/users/auth/google`;
  };

  /**
   * 애플 네이티브 로그인 호출 함수
   */
  const signInWithApple = async () => {
    if (window.webkit) {
      try {
        window.webkit?.messageHandlers?.appleLogin.postMessage("");
      } catch (e: any) {
        console.log(e);
        if (e.message === "토큰 재발급 실패")
          openModal({
            type: "login",
            message:
              "연속 14일 간 접속이 없으면\n자동 로그인이 해제돼요. 그전에 또 만나요! 😉",
          });
        else openModal({ type: "guide", message: "오류가 발생했어요!" });
      }
    } else
      window.location.href = `${process.env.REACT_APP_API}/app/users/auth/appleweb`;
  };

  /**
   * 미회원 회원가입 함수
   * @returns
   */
  const signIn = async () => {
    if (!state[JWT_KEY] || !state["brainy_refresh_token"] || !state["status"])
      return;
    else {
      try {
        const data = form.map((element) => ({
          required: element.required,
          agreeYn: element.agreeYn,
          agreementTitle: element.agreementTitle,
        }));

        const { result, message } = await postAgreement(data, state[JWT_KEY]);

        if (result) {
          window.localStorage.setItem(JWT_KEY, state[JWT_KEY]);
          window.localStorage.setItem(
            "brainy_refresh_token",
            state["brainy_refresh_token"]
          );
          window.localStorage.setItem("INACTIVE", "true");

          navigate("/survey/age", {
            state: {
              loginType: getUserAgent() === "apple" ? "apple" : "google",
            },
          });
        } else openModal({ type: "guide", message: message });
      } catch (e: any) {
        console.log(e);
        if (e.message === "토큰 재발급 실패")
          openModal({
            type: "login",
            message:
              "연속 14일 간 접속이 없으면\n자동 로그인이 해제돼요. 그전에 또 만나요! 😉",
          });
        else openModal({ type: "guide", message: "오류가 발생했어요!" });
      }
    }
  };

  /**
   * 약관 전체 선택 / 해제 함수
   */
  const checkAll = () => {
    if (form.every((term) => term.agreeYn === "Y")) {
      setForm((prev) => prev.map((term) => ({ ...term, agreeYn: "N" })));
    } else if (form.every((term) => term.agreeYn === "N")) {
      setForm((prev) => prev.map((term) => ({ ...term, agreeYn: "Y" })));
    } else if (form.some((term) => term.agreeYn === "Y")) {
      setForm((prev) => prev.map((term) => ({ ...term, agreeYn: "Y" })));
    } else {
      setForm((prev) => prev.map((term) => ({ ...term, agreeYn: "N" })));
    }
  };

  const getUser = async (
    jwt: string
  ): Promise<
    | {
        email: string;
        loginType: "apple" | "appleweb" | "google" | "brainycards";
        id: string;
      }
    | ""
  > => {
    try {
      const {
        isSuccess,
        result: { email, loginType, id },
        message,
      } = await getUserInfo(jwt);

      if (isSuccess) {
        window.localStorage.setItem("userId", id);

        if (loginType === "appleweb" && window.Android) {
          window.Android?.registerDeviceToken();
        }
        return { email: email, loginType: loginType, id: id };
      } else {
        openModal({ type: "guide", message: message });
        return "";
      }
    } catch (e: any) {
      console.log(e);
      if (e.message === "토큰 재발급 실패")
        openModal({
          type: "login",
          message:
            "연속 14일 간 접속이 없으면\n자동 로그인이 해제돼요. 그전에 또 만나요! 😉",
        });
      else openModal({ type: "guide", message: "오류가 발생했어요!" });

      return "";
    }
  };

  const getUsersProcess = async (
    token: string,
    refreshToken: string,
    status: Status
  ) => {
    try {
      const userInfo = await getUser(token);

      const { isSuccess, result, message } = await getUserProcess({
        email: userInfo != "" ? userInfo.email : "",
        loginType: userInfo != "" ? userInfo.loginType : "brainycards",
      });

      if (isSuccess) {
        if (result.agreementYN === "Y" && result.surveyYN === "Y") {
          await patchUserStatus(token);
          window.localStorage.setItem(JWT_KEY, token);
          window.localStorage.setItem("brainy_refresh_token", refreshToken);
          navigate("/");
        } else if (result.agreementYN === "N" && result.surveyYN === "N") {
          navigate(`/login/${userInfo != "" ? userInfo.loginType : ""}`, {
            state: {
              [JWT_KEY]: token,
              brainy_refresh_token: refreshToken,
              status,
            },
          });
        } else if (result.agreementYN === "Y" && result.surveyYN === "N") {
          // if (state && state.again) {
          //   navigate(`/login/${loginType}`, {
          //     state: {
          //       [JWT_KEY]: token,
          //       brainy_refresh_token: refreshToken,
          //       status,
          //     },
          //   });
          // } else {
          // }
          window.localStorage.setItem(JWT_KEY, token);
          window.localStorage.setItem("brainy_refresh_token", refreshToken);
          window.localStorage.setItem("INACTIVE", "true");
          navigate(`/survey/age`, {
            state: {
              loginType: userInfo != "" ? userInfo.loginType : "",
            },
          });
        }
      } else openModal({ type: "guide", message: message });
    } catch (e: any) {
      console.log(e);
      if (e.message === "토큰 재발급 실패")
        openModal({
          type: "login",
          message:
            "연속 14일 간 접속이 없으면\n자동 로그인이 해제돼요. 그전에 또 만나요! 😉",
        });
      else openModal({ type: "guide", message: "오류가 발생했어요!" });
    }
  };

  useEffect(() => {
    /**
     * 애플 네이티브 로그인 호출 함수
     * @param data 액세스 토큰, 리프레시토큰, 유저 가입 상태
     */
    const appleLoginAtiOS = (data: ISocialLoginResponse) => {
      if (data.status === "ACTIVE") {
        window.localStorage.setItem(JWT_KEY, data.accessToken);
        window.localStorage.setItem("brainy_refresh_token", data.refreshToken);
        navigate("/");
      } else {
        getUsersProcess(data.accessToken, data.refreshToken, data.status);
      }
    };

    const googleLoginAtiOS = (data: ISocialLoginResponse) => {
      if (data.status === "ACTIVE") {
        window.localStorage.setItem(JWT_KEY, data.accessToken);
        window.localStorage.setItem("brainy_refresh_token", data.refreshToken);
        navigate("/");
      } else {
        getUsersProcess(data.accessToken, data.refreshToken, data.status);
      }
    };

    window.iOSAppleLogin = appleLoginAtiOS;
    window.iOSGoogleLogin = googleLoginAtiOS;
  }, []);

  useEffect(() => {
    if (isLoggedIn()) {
      navigate("/");
      return;
    }

    // 파라미터 있는 경우 (구글 웹/앱 로그인 리다이렉트)
    if (
      params.get("refreshToken") &&
      params.get("accessToken") &&
      params.get("status")
    ) {
      const token = params.get("accessToken") as string;
      const refreshToken = params.get("refreshToken") as string;
      const status = params.get("status") as Status;

      if (params.get("status") === "ACTIVE") {
        window.localStorage.setItem(JWT_KEY, token);
        window.localStorage.setItem("brainy_refresh_token", refreshToken);
        navigate("/");
      }
      getUsersProcess(token, refreshToken, status);
    } else if (
      !state ||
      !state[JWT_KEY] ||
      !state["brainy_refresh_token"] ||
      !state["status"]
    ) {
      navigate("/login");
      return;
    } else if (
      state &&
      state[JWT_KEY] &&
      state["brainy_refresh_token"] &&
      state["status"]
    ) {
      controlContainer("open");
      return;
    } else navigate("/");
  }, [pathname, state]);

  return (
    <LoginStyled>
      <img className="mascot" src={iconMascot} alt="mascot" />
      <div className="top">
        <img className="logo" src={iconLogo} alt="logo" />
        <h3>
          나도 모르는 사이 카드들을 외워낸
          <br />내 모습을 보고 싶다면?
        </h3>
        <p>
          말만 들었던 뇌과학 기반 학습법,
          <br />
          브레이니카드가 쉽게 제공해드려요.
        </p>
      </div>
      <div className="login-buttons">
        <div className="text">자유로운 기기 연동을 위해 로그인해주세요!</div>
        <div className="buttons">
          <button type="button" onClick={signInWithGoogle}>
            <img className="logo" src={iconGoogleLogo} alt="" />
            <span>Sign in with Google</span>
          </button>

          <button type="button" onClick={signInWithApple}>
            <img className="logo" src={iconAppleLogo} alt="" />
            <span>Sign in with Apple</span>
          </button>
        </div>
      </div>
      <div
        className={`dimmed${dimmed ? " visible" : ""}`}
        onClick={() => controlContainer("close")}
      />
      <div className={`terms-container${opened ? " visible" : ""}`}>
        <div className="controller" onClick={() => controlContainer("close")} />
        <div className="title">
          원활한 사용을 위해
          <br />
          약관에 동의해 주세요.
        </div>
        <div className="input-container">
          <Checkbox
            label="all"
            text="모두 동의"
            checked={form.every((term) => term.agreeYn === "Y")}
            setChecked={checkAll}
          />
          <div className="divider" />
          {form.map((term, idx) => (
            <Checkbox
              key={idx}
              label={term.id || ""}
              text={term.label}
              checked={term.agreeYn === "Y"}
              setChecked={() =>
                setForm((prev) =>
                  prev.map((el) => {
                    if (el.label === term.label) {
                      return { ...el, agreeYn: el.agreeYn === "Y" ? "N" : "Y" };
                    } else return { ...el };
                  })
                )
              }
            />
          ))}
        </div>
        <button
          type="button"
          className="join-button"
          disabled={
            !form
              .filter((term) => term.required === "Y")
              .every((term) => term.agreeYn === "Y")
          }
          onClick={signIn}
        >
          가입 완료
        </button>
      </div>
    </LoginStyled>
  );
}

const LoginStyled = styled.div`
  min-width: 36rem;
  min-height: 100dvh;
  height: 100%;
  width: 100%;
  padding: 0 2rem;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;

  .mascot {
    position: absolute;
    z-index: -1;
    right: 0;
    bottom: 9.6rem;
  }

  .top {
    & > .logo {
      margin-top: 9rem;
      margin-bottom: 2.4rem;
    }

    h3 {
      margin-bottom: 0.8rem;
    }

    p {
      font-size: 1.4rem;
      color: var(--color-grey-500);
    }
  }

  .login-buttons {
    display: flex;
    flex-direction: column;
    row-gap: 1.6rem;
    margin-bottom: 4.9rem;
    text-align: center;

    .text {
      font-size: 1.4rem;
      color: var(--color-grey-400);
    }

    .buttons {
      display: flex;
      flex-direction: column;
      row-gap: 1.2rem;

      button {
        width: 100%;
        background-color: var(--color-white);
        display: flex;
        justify-content: center;
        align-items: center;
        column-gap: 2.4rem;
        padding: 1.5rem 0;
        border: 0.1rem solid var(--color-grey-200);
        border-radius: 4rem;
        font-size: 1.4rem;
        font-weight: 500;
        color: var(--color-grey-700);
      }
    }
  }

  .dimmed {
    z-index: -1;
    position: fixed;
    inset: 0;
    transition: all 0.2s ease-in-out;

    &.visible {
      z-index: 50;
      background-color: rgba(0, 0, 0, 0.4);
    }
  }

  .terms-container {
    position: fixed;
    bottom: -1000%;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 36rem;
    height: 48rem;
    background-color: white;
    z-index: 51;
    transition: all 0.2s ease-in-out;
    border-radius: 1.2rem 1.2rem 0 0;
    display: flex;
    flex-direction: column;
    padding: 0 2rem;
    box-shadow: 0px 4px 19px 0px #00000040;

    &.visible {
      bottom: 0;
    }

    .controller {
      width: 6rem;
      height: 0.4rem;
      margin: 1.2rem auto 4.3rem;
      background-color: var(--color-grey-200);
      border-radius: 10rem;
    }

    .title {
      font-size: 2rem;
      font-weight: 600;
      color: var(--color-grey-900);
      letter-spacing: 0;
      margin-bottom: 4rem;
    }

    .input-container {
      display: flex;
      flex-direction: column;
      row-gap: 2.4rem;
      margin-bottom: 5.6rem;

      & > fieldset:first-of-type {
        & > label > div {
          font-size: 1.6rem;
          font-weight: 700;
        }
      }

      .divider {
        width: 100%;
        height: 0.075rem;
        background-color: var(--color-grey-300);
      }
    }

    .join-button {
      height: 5rem;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      color: var(--color-white);
      background-color: var(--color-main);
      transition: all 0.2s ease-in-out;
      border-radius: 0.8rem;
      font-size: 1.6rem;
      font-weight: 600;

      &:disabled {
        background-color: #d2d7dc;
      }
    }
  }
`;
